import type { ClipTranscriptToken } from '../types';

const SENTENCE_ENDING_PUNCT = new Set(['.', '!', '?']);

/**
 * Checks if a punctuation value ends a sentence.
 */
export function isSentenceEndingPunct(value: string | null): boolean {
  return value != null && SENTENCE_ENDING_PUNCT.has(value);
}

/**
 * Capitalizes the first character of a string.
 */
export function capitalizeFirstChar(value: string): string {
  if (value.length === 0) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Decapitalizes the first character of a string.
 */
export function decapitalizeFirstChar(value: string): string {
  if (value.length === 0) return value;
  return value.charAt(0).toLowerCase() + value.slice(1);
}

/**
 * Heuristic: returns true if a word looks like sentence capitalization
 * (first char upper, rest lower) rather than an acronym ("FBI", "NASA")
 * or camelCase ("iPhone").
 *
 * Only words matching this heuristic are candidates for decapitalization.
 */
export function isLikelySentenceCapitalized(value: string): boolean {
  if (value.length === 0) return false;
  const firstChar = value.charAt(0);
  // First character must be uppercase
  if (
    firstChar !== firstChar.toUpperCase() ||
    firstChar === firstChar.toLowerCase()
  ) {
    return false;
  }
  // Single character — treat as sentence-cap
  if (value.length === 1) return true;
  // If second character is also uppercase (and is a letter), likely an acronym
  const secondChar = value.charAt(1);
  if (
    secondChar === secondChar.toUpperCase() &&
    secondChar !== secondChar.toLowerCase()
  ) {
    return false;
  }
  return true;
}

/**
 * Finds the first text token in a token array.
 */
export function findFirstTextToken(
  tokens: readonly ClipTranscriptToken[],
): ClipTranscriptToken | null {
  return tokens.find(t => t.type === 'text' && t.state !== 'removed') ?? null;
}

/**
 * Given a token array, returns a new token with its first character capitalized.
 * If no change is needed, returns the original token.
 */
export function capitalizeToken(
  token: ClipTranscriptToken,
): ClipTranscriptToken {
  if (token.type !== 'text' || !token.value) return token;
  const capitalized = capitalizeFirstChar(token.value);
  if (capitalized === token.value) return token;
  return { ...token, value: capitalized };
}

/**
 * Given a token array, returns a new token with its first character decapitalized.
 * Only applies if the word looks like sentence capitalization (not an acronym).
 * If no change is needed, returns the original token.
 */
export function decapitalizeToken(
  token: ClipTranscriptToken,
): ClipTranscriptToken {
  if (token.type !== 'text' || !token.value) return token;
  if (!isLikelySentenceCapitalized(token.value)) return token;
  const decapitalized = decapitalizeFirstChar(token.value);
  if (decapitalized === token.value) return token;
  return { ...token, value: decapitalized };
}

/**
 * Returns true if the given token is preceded by sentence-ending punctuation
 * in the token array (skipping whitespace/removed tokens).
 */
export function isPrecededBySentenceEndingPunct(
  tokens: readonly ClipTranscriptToken[],
  tokenId: string,
): boolean {
  const idx = tokens.findIndex(t => t.id === tokenId);
  if (idx <= 0) return false;

  for (let i = idx - 1; i >= 0; i--) {
    const prev = tokens[i];
    if (prev.state === 'removed') continue;
    if (prev.type === 'text') return false;
    if (prev.type === 'punct' && isSentenceEndingPunct(prev.value)) return true;
    // Non-sentence-ending punct (comma, whitespace) — keep scanning
  }
  return false;
}
